import React, { useState, useEffect, useRef, useCallback } from 'react'; // Add useCallback
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import './DetailComponents.css';
import API_BASE_URL from '../config';

const AddTripModal = ({ onSave, onClose }) => {
  const [newTrip, setNewTrip] = useState({
    clientId: '',
    title: '',
    destinations: [''],
    startDate: null,
    endDate: null
  });
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClientName, setSelectedClientName] = useState('');
  const [showClientList, setShowClientList] = useState(false);
  const modalRef = useRef(null);

  const handleClickOutside = useCallback((event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  // Handle ESC key press
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [onClose]);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/clients`)
      .then(response => {
        const data = response.data.data || [];
        const validClients = data.filter(client => {
          if (!client || typeof client !== 'object' || !client._id || !client.name || !client.lastName1) {
            return false;
          }
          return true;
        });
        setClients(validClients);
      })
      .catch(() => setClients([]));
  }, []);

  useEffect(() => {
    if (searchQuery && Array.isArray(clients)) {
      const lowerQuery = searchQuery.toLowerCase();
      const filtered = clients.filter(client => {
        if (!client || typeof client !== 'object') return false;
        try {
          const fullName = `${client.name || ''} ${client.midName || ''} ${client.lastName1 || ''} ${client.lastName2 || ''}`.toLowerCase().trim();
          return fullName.includes(lowerQuery);
        } catch {
          return false;
        }
      });
      setFilteredClients(filtered);
      setShowClientList(true);
    } else {
      setFilteredClients([]);
      setShowClientList(false);
    }
  }, [searchQuery, clients]);

  const handleClientSelect = (client) => {
    setNewTrip({ ...newTrip, clientId: client._id });
    setSelectedClientName(`${client.name} ${client.midName || ''} ${client.lastName1} ${client.lastName2 || ''}`.trim());
    setSearchQuery('');
    setShowClientList(false);
  };

  const handleSave = async () => {
    if (!newTrip.clientId || !newTrip.title || !newTrip.startDate || !newTrip.endDate) {
      alert('Please fill in all required fields');
      return;
    }
    
    // Check if at least one destination is filled
    const hasValidDestination = newTrip.destinations.some(dest => dest.trim() !== '');
    if (!hasValidDestination) {
      alert('Please add at least one destination');
      return;
    }
    if (!(newTrip.startDate instanceof Date) || isNaN(newTrip.startDate.getTime())) {
      alert('Invalid Start Date');
      return;
    }
    if (!(newTrip.endDate instanceof Date) || isNaN(newTrip.endDate.getTime())) {
      alert('Invalid End Date');
      return;
    }
    if (newTrip.endDate < newTrip.startDate) {
      alert('End Date cannot be before Start Date');
      return;
    }

    try {
      const filteredDestinations = newTrip.destinations.filter(dest => dest.trim() !== '');
      const tripData = {
        clientId: newTrip.clientId,
        title: newTrip.title,
        destinations: filteredDestinations,
        startDate: newTrip.startDate.toISOString(),
        endDate: newTrip.endDate.toISOString(),
        showItinerary: false
      };

      const response = await axios.post(`${API_BASE_URL}/api/trips`, tripData);
      onSave(response.data.data);
      onClose();
    } catch (error) {
      console.error('Error creating trip:', error);
      alert('Failed to create trip. Please try again.');
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal edit-modal" ref={modalRef}>
        <h3>Add Itinerary</h3>
        <div className="edit-modal-form">
          <div className="edit-modal-field" style={{ position: 'relative' }}>
            <label className="edit-modal-label">Client:</label>
            <input
              className="edit-modal-input"
              value={searchQuery || selectedClientName}
              onChange={e => {
                setSearchQuery(e.target.value);
                setSelectedClientName('');
                setNewTrip({ ...newTrip, clientId: '' });
              }}
              placeholder="Search by name or last name"
            />
            {showClientList && filteredClients.length > 0 && (
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                border: '1px solid #ccc',
                borderRadius: '3px',
                maxHeight: '150px',
                overflowY: 'auto',
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: '#fff',
                zIndex: 1000,
                marginTop: '5px'
              }}>
                {filteredClients.map(client => (
                  <li
                    key={client._id}
                    style={{
                      padding: '8px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                      textAlign: 'left'
                    }}
                    onClick={() => handleClientSelect(client)}
                  >
                    {client.name} {client.midName || ''} {client.lastName1} {client.lastName2 || ''}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="edit-modal-field">
            <label className="edit-modal-label">Title:</label>
            <input
              className="edit-modal-input"
              value={newTrip.title}
              onChange={e => setNewTrip({ ...newTrip, title: e.target.value })}
              placeholder="Enter itinerary title"
            />
          </div>
          <div className="edit-modal-field">
            <label className="edit-modal-label">Destinations</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
              <input
                className="edit-modal-input"
                type="text"
                placeholder="Enter destination (e.g., Hawaii, Paris, Tokyo)"
                style={{ flex: 1 }}
                autoComplete="off"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const value = e.target.value.trim();
                    if (value) {
                      setNewTrip({ ...newTrip, destinations: [...newTrip.destinations, value] });
                      e.target.value = '';
                    }
                  }
                }}
              />
              <button
                type="button"
                onClick={() => {
                  const input = document.querySelector('input[placeholder*="Enter destination"]');
                  const value = input?.value.trim();
                  if (value) {
                    setNewTrip({ ...newTrip, destinations: [...newTrip.destinations, value] });
                    input.value = '';
                  }
                }}
                className="add-button"
                title="Add destination"
              >
                +
              </button>
            </div>

            {/* Display added destinations */}
            {newTrip.destinations.filter(dest => dest.trim() !== '').length > 0 && (
              <div style={{ marginTop: '8px' }}>
                {newTrip.destinations
                  .map((dest, index) => dest.trim() !== '' ? (
                    <span key={index} style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      marginRight: '8px',
                      marginBottom: '4px',
                      padding: '2px 6px',
                      backgroundColor: '#e9ecef',
                      borderRadius: '3px',
                      fontSize: '0.8em'
                    }}>
                      {dest}
                      <button
                        type="button"
                        onClick={() => {
                          const updatedDestinations = newTrip.destinations.filter((_, i) => i !== index);
                          setNewTrip({ ...newTrip, destinations: updatedDestinations });
                        }}
                        style={{
                          marginLeft: '4px',
                          padding: '0 2px',
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: '#6c757d',
                          cursor: 'pointer',
                          fontSize: '0.9em',
                          lineHeight: 1
                        }}
                        title="Remove destination"
                      >
                        ×
                      </button>
                    </span>
                  ) : null)
                  .filter(Boolean)
                }
              </div>
            )}
          </div>
          <div className="edit-modal-field">
            <label className="edit-modal-label">Start Date:</label>
            <DatePicker
              selected={newTrip.startDate}
              onChange={date => setNewTrip({ ...newTrip, startDate: date })}
              dateFormat="MMMM d, yyyy"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              className="edit-modal-input"
              placeholderText="Select Start Date"
            />
          </div>
          <div className="edit-modal-field">
            <label className="edit-modal-label">End Date:</label>
            <DatePicker
              selected={newTrip.endDate}
              onChange={date => setNewTrip({ ...newTrip, endDate: date })}
              dateFormat="MMMM d, yyyy"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              minDate={newTrip.startDate}
              className="edit-modal-input"
              placeholderText="Select End Date"
            />
          </div>
        </div>
        <div className="modal-actions">
          <button className="modal-action-button" onClick={onClose}>Cancel</button>
          <button className="modal-action-button" onClick={handleSave}>Save</button>
        </div>
      </div>
    </div>
  );
};

export default AddTripModal;